import { TAGSvgIcon, useFacilityContext } from '@aspendental/shared-components-web';
import {
	Box,
	Button,
	Card,
	CardContent,
	Grid,
	Link,
	Stack,
	SvgIcon,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';

import { addToCalendar } from './Confirmation.helpers';
import { linkStyles } from './Confirmation.styles';
import MarketingCard from './MarketingCard';
import { REASON_FOR_VISIT_ID } from '../ReasonForVisit/ReasonForVisit.helpers';

import PhoneNumberLink from '@src/components/PhoneNumberLink';
import { DataLayerStepNames, FALLBACK_FACILITY_PHONE_NUMBER } from '@src/constants';
import { useCopyToClipboard } from '@src/hooks/useCopyToClipboard';
import { CalendarDateTime } from '@src/icons';
import { dataLayer } from '@src/services/dataLayer';
import { useAppStore, useAppointmentStore, usePatientInformationFormStore, useReasonForVisitStore } from '@src/store';
import { trimTimeZoneOffset } from '@src/utils/trimTimeZoneOffset';
import { SuccessCard } from 'types/generated';

export default function Confirmation({
	successCardsCollection,
}: {
	successCardsCollection: { items: Array<SuccessCard> };
}) {
	const { mottoFlow } = useAppStore((store) => store);
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
	const [, copyToClipboard] = useCopyToClipboard();
	const [isCopied, setIsCopied] = useState(false);

	const { appointmentSlot, appointmentFacility, appointmentNumber } = useAppointmentStore((store) => store);
	const { accountNumber, hasInsurance } = usePatientInformationFormStore((store) => store);
	const { reasonForVisit } = useReasonForVisitStore((store) => store);
	const webFacilityDetails = useFacilityContext();
	const { t } = useTranslation('confirmation');
	const router = useRouter();
	const { locale } = router;

	const location = `${appointmentFacility?.address?.line1} ${appointmentFacility?.address?.city}, ${appointmentFacility?.address?.state}  ${appointmentFacility?.zipCode}`;

	useEffect(() => {
		dataLayer({
			language_preference: locale,
			brand: mottoFlow ? 'mottoaligners' : 'aspendental',
			lead_score: appointmentSlot?.patientValue,
			lead_tier_national: appointmentSlot?.nationalPatientTier,
			lead_tier_office: appointmentSlot?.patientTier,
			step_name: DataLayerStepNames.confirmation,
			reason_for_visit: reasonForVisit,
			reason_for_visit_id: REASON_FOR_VISIT_ID[reasonForVisit],
			dental_insurance: String(!!hasInsurance),
			...(webFacilityDetails?.officeInfo?.code && {
				website_selected_facility_id: webFacilityDetails?.officeInfo?.code,
			}),
			...(webFacilityDetails?.officeInfo?.name && {
				website_selected_facility_name: webFacilityDetails?.officeInfo?.name,
			}),
			...(appointmentFacility?.facilityCode && {
				appointment_facility_id: appointmentFacility?.facilityCode,
			}),
			...(appointmentFacility?.title && {
				appointment_facility_name: appointmentFacility?.title,
			}),
			account_id: accountNumber,
			appointment_id: appointmentNumber,
		});
	}, [
		reasonForVisit,
		webFacilityDetails?.officeInfo?.code,
		webFacilityDetails?.officeInfo?.name,
		appointmentFacility?.facilityCode,
		appointmentFacility?.title,
		hasInsurance,
		accountNumber,
		appointmentNumber,
		appointmentSlot,
		locale,
		mottoFlow,
	]);

	return (
		<>
			<Grid container spacing={2} direction={{ xs: 'column-reverse', sm: 'row' }}>
				<Grid item xs={12} lg={5}>
					<Card elevation={0} variant="outlined" sx={{ bgcolor: 'backgrounds.neutral' }}>
						<CardContent>
							<Grid container direction={{ xs: 'column', sm: 'row' }}>
								<Grid item xs={12} sm={2} alignSelf={{ xs: 'center', sm: 'start' }}>
									<TAGSvgIcon icon="AspenOffice" />
								</Grid>
								<Grid item xs={12} sm={10}>
									<Stack alignItems={{ xs: 'center', sm: 'flex-start' }}>
										<Typography
											variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'}
											color="text.primary"
										>
											{appointmentFacility?.address?.line1}
										</Typography>
										<Typography
											variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'}
											color="text.primay"
										>{`${appointmentFacility?.address?.city}, ${appointmentFacility?.address?.state}  ${appointmentFacility?.zipCode}`}</Typography>
										<PhoneNumberLink
											phoneNumber={appointmentFacility?.phone ?? FALLBACK_FACILITY_PHONE_NUMBER}
											variant={isSmallScreen ? 'bodyMediumBold' : 'bodyLargeBold'}
										/>
									</Stack>
								</Grid>
							</Grid>
							<Grid container direction={{ xs: 'column', sm: 'row' }} mt={2}>
								<Grid item xs={12} sm={2} alignSelf={{ xs: 'center', sm: 'start' }}>
									<SvgIcon viewBox="0 0 32 32" sx={{ height: 32, width: 32 }}>
										<CalendarDateTime />
									</SvgIcon>
								</Grid>
								<Grid item xs={12} sm={10}>
									<Stack alignItems={{ xs: 'center', sm: 'flex-start' }}>
										<Typography
											variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'}
											color="text.primary"
										>
											{new Intl.DateTimeFormat(locale, {
												weekday: 'long',
												year: 'numeric',
												month: 'long',
												day: 'numeric',
											}).format(new Date(trimTimeZoneOffset(appointmentSlot?.time) ?? ''))}
										</Typography>
										<Typography
											variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'}
											color="text.primary"
										>
											{new Intl.DateTimeFormat(locale, {
												hour: '2-digit',
												minute: '2-digit',
											}).format(new Date(trimTimeZoneOffset(appointmentSlot?.time) ?? ''))}
										</Typography>
										<Link
											// inputProps={{
											// 	'data-test-id': 'test'
											// }}
											data-test-id="add-to-calendar-button"
											variant={isSmallScreen ? 'bodyMediumBold' : 'bodyLargeBold'}
											color="secondary.main"
											title="addToCalendar"
											href="#"
											sx={linkStyles}
											onClick={() =>
												addToCalendar(appointmentSlot?.time ?? '', reasonForVisit, location)
											}
										>
											{t('addToCalendar')}
										</Link>
									</Stack>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
				<Grid item xs={12} lg={7}>
					<Card elevation={0} variant="outlined" sx={{ bgcolor: 'backgrounds.neutral', height: '100%' }}>
						<CardContent>
							<Grid
								container
								direction={{ xs: 'column', sm: 'row' }}
								alignItems={{ xs: 'center', sm: 'start' }}
							>
								<Grid item xs={12} sm={5}>
									<Stack direction={{ xs: 'column' }} alignItems={{ xs: 'center', sm: 'flex-start' }}>
										<Typography
											variant="overline"
											color="text.primary"
											sx={{ lineHeight: '1.75rem' }}
										>
											{t('acctNumber1')}
										</Typography>
										<Typography variant="header4" color="text.primary">
											{accountNumber}
										</Typography>
									</Stack>
								</Grid>
								<Grid item>
									<Tooltip
										title={
											<span aria-live="assertive">
												<Box sx={visuallyHidden}>{t('acctNumber2')} </Box>
												{t('copied')}
											</span>
										}
										open={isCopied}
										disableFocusListener
										disableHoverListener
										disableTouchListener
										onClose={() => null}
										describeChild
									>
										<Button
											data-test-id="copy-account-number-button"
											size="M"
											variant="secondaryDefault"
											onClick={() => {
												copyToClipboard(accountNumber);
												setIsCopied(true);
												setTimeout(() => {
													setIsCopied(false);
												}, 1500);
											}}
											sx={{ mt: 0.75 }}
										>
											{t('copy')}&nbsp;
											<Box sx={visuallyHidden}>{t('acctNumber3')}</Box> {t('toClip')}
										</Button>
									</Tooltip>
								</Grid>
								<Grid item xs={12} mt={2}>
									<Typography
										variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'}
										color="text.primary"
									>
										{t('takeNote')}
									</Typography>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			<Grid container spacing={2} mt={2}>
				{successCardsCollection?.items.map((card) => {
					if (card) {
						return <MarketingCard card={card} key={card?.sys.id} />;
					}
				})}
			</Grid>
		</>
	);
}
